import { useState, useEffect } from 'react';
import { Box, Typography, Button, Grid, CircularProgress, Alert, Snackbar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FileDownload } from '@mui/icons-material';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import StatCard from '../components/rewards/StatCard';
import ReviewsTable from '../components/reviews/ReviewsTable';
import { getFullUserData, getUserBusinessName } from '../utils/userHelper';
import { getReviews, exportReviews } from '../services/reviewsService';
import type { ReviewData } from '../types/review';

const PageContainer = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  bgcolor: '#F8F9FA'
});

const MainContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

const ContentArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '1400px',
  margin: '0 auto',
  width: '100%'
}));

const Reviews = () => {
  const userData = getFullUserData();
  const businessName = getUserBusinessName();
  
  const [reviews, setReviews] = useState<ReviewData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch reviews on mount
  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getReviews(100, 0);

      if (response.success && response.data && response.data.reviews) {
        setReviews(response.data.reviews);
        console.log(`✅ Loaded ${response.data.reviews.length} reviews (Total: ${response.data.total})`);
      } else if (response.success && response.data && !response.data.reviews) {
        // Backend returned success but no reviews (empty list)
        setReviews([]);
        console.log('ℹ️ No reviews found');
      } else {
        throw new Error(response.message || 'Failed to fetch reviews');
      }
    } catch (err) {
      console.error('❌ Error fetching reviews:', err);
      setError(err instanceof Error ? err.message : 'Failed to load reviews');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      setError(null);
      const blob = await exportReviews();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reviews-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setSuccess('Reviews exported successfully!');
    } catch (err) {
      console.error('Error exporting reviews:', err);
      setError(err instanceof Error ? err.message : 'Failed to export reviews');
    }
  };

  // Calculate stats
  const pendingClaims = reviews.filter(r => r.reward_claim_status === 'pending').length;
  const paidRewards = reviews.filter(r => r.reward_claim_status === 'paid').length;
  const totalPosted = reviews.filter(r => r.survey_completed && r.posted_at).length;

  return (
    <PageContainer>
      <Sidebar user={userData} />
      
      <MainContent>
        <DashboardHeader
          businessName={businessName}
          userAvatar={userData.avatar}
          businessLogo={userData.businessLogo}
        />
        
        <ContentArea>
          {/* Page Heading */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 4
            }}
          >
            <Box sx={{ minWidth: 288 }}>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '1.875rem', md: '2.25rem' },
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  color: '#111827',
                  mb: 1
                }}
              >
                Customer Reviews
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '1rem',
                  color: 'text.secondary'
                }}
              >
                View all customer reviews and reward claim statuses.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={handleExportData}
              sx={{
                minWidth: 84,
                height: 40,
                color: 'text.primary',
                borderColor: '#E5E7EB',
                backgroundColor: '#F9FAFB',
                fontWeight: 700,
                fontSize: '0.875rem',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                  borderColor: '#D1D5DB'
                }
              }}
            >
              Export Data
            </Button>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Total Reviews"
                value={reviews.length}
                change={0}
                trend="up"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Posted on Platform"
                value={totalPosted}
                change={0}
                trend="up"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Pending Claims"
                value={pendingClaims}
                change={0}
                trend="down"
              />
            </Grid>
          </Grid>

          {/* Reviews Table */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : (
            <ReviewsTable
              reviews={reviews}
              onExportData={handleExportData}
            />
          )}
        </ContentArea>
      </MainContent>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default Reviews;

